import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect, useDispatch } from 'react-redux';

import { ticketDocumentVersionsSet, ticketStagesSet } from '~/actions/ticket';
import { getTicketDocumentVersions, uploadSignedDocument } from '~/api/tickets';
import { documentTypeIcon } from '~/components/DocumentsPage/Document.utils';
import EcCheckbox from '~/components/Shared/EcCheckbox/EcCheckbox';
import { Button } from '~/eds/src/components/Button/Button';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { User } from '~/eds/src/components/User/User';
import { FileExtensionType } from '~/enums/FileExtensionType';
import { TicketStageType } from '~/enums/TicketStageType';
import { TicketStatusType } from '~/enums/TicketStatusType';
import {
  EsignatureEnvelopeStatusValue,
  EsignatureSignerStatusValue,
} from '~/features/ticket/types/esignature';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import * as TAGS from '~/redux/api/endpoints/allEndpoints';
import { TagType } from '~/redux/api/TagType';
import Box from '~/ui/components/Box/Box';
import FileInput from '~/ui/components/FileInput/FileInput';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Switch from '~/ui/components/Switch/Switch';
import Text from '~/ui/components/Text/Text';
import { getFileExtension } from '~/utils/files';
import {
  shouldFillFormQuestionsInSignStage,
  shouldFillFormQuestionsInSignStageEsignatureRevamp,
  testHasInvalidSigners,
  testHasInvalidSignersEsignRevamp,
  testIsCcSigner,
} from '~/utils/ticket';
import { testIsEverestAdmin } from '~/utils/user';
import { getAcceptedFiles } from '~/utils/workflow';

const defaultFileExtensions = [FileExtensionType.Docx, FileExtensionType.Pdf];
const defaultEsignRevampRecipients = { signers: [], carbonCopies: [] };

function getSignedUserIds(approvals) {
  return Object.values(approvals)
    .filter(({ status }) => status === TicketStatusType.Signed)
    .map(({ externalUser, userId }) => (!!userId ? userId : externalUser.id));
}

function UploadSignedButton({ approvals, phases, ticket }) {
  const { toast } = useToast();
  const intl = useIntl();

  const signersList = useMemo(() => {
    return Object.values(approvals)
      .filter(
        (approval) => phases[approval.phaseId].stage === TicketStageType.Sign,
      )
      .filter((approval) => !testIsCcSigner(approval));
  }, [approvals]);

  const [
    resolveUsers,
    {
      data: users = {},
      isSuccess: hasResolvedUsers,
      isLoading: isLoadingResolveUsers,
    },
  ] = api.endpoints.resolveUsers.useLazyQuery();
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();
  const isEsignatureV2RevampEnabled = useFlag(FlagType.EsignatureV2Revamp);
  const isSignStage = ticket.stage === TicketStageType.Sign;
  const {
    data: esignRevampRecipients = defaultEsignRevampRecipients,
    isLoading: isLoadingEsignRevampRecipients,
    error: errorEsignRevampRecipients,
  } = api.endpoints.getRecipients.useQuery(
    {
      ticketId: ticket.id,
    },
    {
      skip: !ticket.id || !isEsignatureV2RevampEnabled,
    },
  );
  const { data: envelope } = api.endpoints.getEnvelope.useQuery(
    {
      ticketId: ticket.id,
    },
    {
      skip: !ticket.id || !isEsignatureV2RevampEnabled || !isSignStage,
    },
  );

  const [showModal, setShowModal] = useState(false);

  const preRevampSigners = useMemo(() => {
    const userIds = signersList
      .filter((approval) => approval.userId)
      .map((approval) => approval.userId);
    const hasUnresolvedUsers = userIds.some((id) => !users[id]);
    return hasUnresolvedUsers ? [] : userIds.map((userId) => users[userId]);
  }, [signersList, users]);
  const preRevampExternalSigners = useMemo(() =>
    signersList
      .filter((approval) => approval.externalUser)
      .map((approval) => approval.externalUser, [signersList]),
  );

  const esignRevampSigners = useMemo(() => {
    const internalSigners =
      esignRevampRecipients.signers.filter(
        (recipient) => !recipient.isExternal,
      ) ?? [];
    return internalSigners
      .map((recipient) => {
        if (recipient.userId && users[recipient.userId]) {
          return {
            ...users[recipient.userId],
            recipientId: recipient.id,
          };
        }
        if (recipient.external?.email) {
          return {
            id: recipient.id,
            name: recipient.external?.name,
            email: recipient.external?.email,
            recipientId: recipient.id,
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [esignRevampRecipients, users]);
  const esignRevampExternalSigners = useMemo(() => {
    return esignRevampRecipients.signers
      .filter((recipient) => recipient.isExternal)
      .map((recipient) => ({
        id: recipient.id,
        name: recipient.external?.name,
        email: recipient.external?.email,
        recipientId: recipient.id,
      }));
  }, [esignRevampRecipients]);

  const signers = isEsignatureV2RevampEnabled
    ? esignRevampSigners
    : preRevampSigners;
  const externalSigners = isEsignatureV2RevampEnabled
    ? esignRevampExternalSigners
    : preRevampExternalSigners;

  const hasInternalSignersNeedingResolution =
    isEsignatureV2RevampEnabled &&
    esignRevampRecipients.signers.some((r) => !r.isExternal && r.userId);
  const isSignersReady = isEsignatureV2RevampEnabled
    ? !hasInternalSignersNeedingResolution || hasResolvedUsers
    : hasResolvedUsers;

  const [selectedDocument, setSelectedDocument] = useState();
  const initialSelectedSigners = useMemo(
    () =>
      isEsignatureV2RevampEnabled
        ? esignRevampRecipients.signers
            .filter(
              (recipient) =>
                recipient.status?.value === EsignatureSignerStatusValue.Signed,
            )
            .map((recipient) => recipient.id)
        : getSignedUserIds(ticket.approvals),
    [
      isEsignatureV2RevampEnabled,
      esignRevampRecipients.signers,
      ticket.approvals,
    ],
  );
  const [selectedSigners, setSelectedSigners] = useState(
    initialSelectedSigners,
  );

  useEffect(() => {
    setSelectedSigners(initialSelectedSigners);
  }, [initialSelectedSigners]);

  const [isKeepingUploadedFilename, setIsKeepingUploadedFilename] =
    useState(false);
  const hasDeletedUsers = signers.some((signer) => signer.isDeleted);
  const isEnvelopeSigned = isEsignatureV2RevampEnabled
    ? envelope?.status?.value === EsignatureEnvelopeStatusValue.Signed
    : ticket.stages.sign?.status === TicketStatusType.Signed;

  useEffect(() => {
    if (signersList?.length && !isEsignatureV2RevampEnabled) {
      const userIds = signersList
        .filter((approval) => approval.userId)
        .map((approval) => approval.userId);
      if (userIds.length) {
        resolveUsers({
          ids: userIds,
          params: { clientId: currentUser.client, includeDeleted: true },
        });
      }
    }
  }, [signersList, isEsignatureV2RevampEnabled]);

  useEffect(() => {
    if (esignRevampRecipients.signers.length && isEsignatureV2RevampEnabled) {
      const userIds = esignRevampRecipients.signers
        .filter((signer) => signer.userId)
        .map((signer) => signer.userId);
      if (userIds.length) {
        resolveUsers({
          ids: userIds,
          params: { clientId: currentUser.client, includeDeleted: true },
        });
      }
    }
  }, [esignRevampRecipients, isEsignatureV2RevampEnabled]);

  const isMissingIntakeFormSignerData = isEsignatureV2RevampEnabled
    ? shouldFillFormQuestionsInSignStageEsignatureRevamp(
        ticket,
        envelope?.status?.value,
      )
    : shouldFillFormQuestionsInSignStage(ticket, users);

  const isNoSigners = isEsignatureV2RevampEnabled
    ? esignRevampRecipients.signers.length === 0
    : externalSigners.length === 0 && signers.length === 0;

  if (
    !isSignStage ||
    isEnvelopeSigned ||
    isMissingIntakeFormSignerData ||
    isNoSigners
  ) {
    return null;
  }

  function handleOnAttachFiles(files) {
    setSelectedDocument(files[0]);
  }

  function handleOnRemoveFileClick() {
    setSelectedDocument(null);
  }

  function handleOnSignerCheckboxClick(user) {
    let newSelectedUsers = [...selectedSigners];
    // need to use recipient id for esignature v2 revamp, user id for pre revamp
    const userId = isEsignatureV2RevampEnabled ? user.recipientId : user.id;

    if (selectedSigners.includes(userId)) {
      newSelectedUsers = newSelectedUsers.filter((user) => user !== userId);
    } else {
      newSelectedUsers.push(userId);
    }

    setSelectedSigners(newSelectedUsers);
  }

  const actionButton = {
    disabled: hasDeletedUsers || !selectedDocument || !selectedSigners.length,
    tooltip:
      hasDeletedUsers &&
      intl.formatMessage({
        id: 'UploadSignedButton.deactivatedUsersTooltip',
        defaultMessage: 'You have Deactivated users assigned as signers',
        description: 'Tooltip warning about deactivated users as signers',
      }),
    text: intl.formatMessage({
      id: 'UploadSignedButton.uploadButton',
      defaultMessage: 'Upload',
      description: 'Upload button text',
    }),
    variant: 'primary',
    errorHandler: (error) => {
      const additionalErrorMessage = error?.response?.data?.data;
      toast({
        message: intl.formatMessage(
          {
            id: 'UploadSignedButton.uploadError',
            defaultMessage: 'Unable to upload document, {errorMessage}.',
            description:
              'Error message when document upload fails. errorMessage example: "please try again", "file too large"',
          },
          {
            errorMessage:
              additionalErrorMessage ||
              intl.formatMessage({
                id: 'UploadSignedButton.defaultErrorMessage',
                defaultMessage: 'please try again',
                description:
                  'Default error message when no specific error is provided',
              }),
          },
        ),
        status: 'danger',
      });
    },
    promise: async function () {
      const res = await uploadSignedDocument({
        ticketId: ticket.id,
        file: selectedDocument,
        signerIds: selectedSigners,
        isKeepingUploadedFilename: isKeepingUploadedFilename,
        isEsignatureV2RevampEnabled,
      });
      const esigErrorMessage = res?.esigErrorMessage;
      esigErrorMessage
        ? toast({
            message: esigErrorMessage,
            status: 'info',
          })
        : toast({
            message: intl.formatMessage({
              id: 'UploadSignedButton.uploadSuccess',
              defaultMessage: 'Document has been successfully uploaded.',
              description: 'Success message when document is uploaded',
            }),
            status: 'success',
          });
      const documentVersions = await getTicketDocumentVersions(
        ticket.document.id,
      );
      dispatch(ticketDocumentVersionsSet(documentVersions));
      dispatch(
        api.util.invalidateTags([
          { type: TagType.Ticket, id: 'CURRENT_VERSION' },
          TAGS.TAG_BY_TICKET_SUMMARY,
          TAGS.TAG_CURRENT_STAGE,
          {
            type: TagType.EsignatureEnvelope,
            id: ticket.id,
          },
          {
            type: TagType.TicketRecipients,
            id: ticket.id,
          },
        ]),
      );
    },
  };

  function renderUser(user) {
    const signerUserStyle = {
      '&:not(:last-child)': {
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
      },
    };
    const name = user.firstName
      ? { firstName: user.firstName, lastName: user.lastName }
      : { firstName: user.name || user.email };

    const userInfo = {
      ...name,
      email: user.email,
      id: user.id,
    };
    const selectedId = isEsignatureV2RevampEnabled ? user.recipientId : user.id;

    return (
      <FlexLayout
        key={user.id}
        alignItems="center"
        py={3}
        space={4}
        sx={signerUserStyle}
      >
        <EcCheckbox
          checked={selectedSigners.includes(selectedId)}
          onClick={() => handleOnSignerCheckboxClick(user)}
        />
        <User
          mode="avatar-name"
          user={userInfo}
          options={{
            isCurrentUser: user.id === currentUser.id,
            isDeleted: user.isDeleted,
          }}
        />
      </FlexLayout>
    );
  }

  function renderUsersField() {
    const isLoading = isEsignatureV2RevampEnabled
      ? isLoadingEsignRevampRecipients || isLoadingResolveUsers
      : isLoadingResolveUsers;

    return (
      <FlexLayout flexDirection="column" space={2}>
        <Text variant="xs-dense-caps" color="gray-900">
          {intl.formatMessage({
            id: 'UploadSignedButton.signersLabel',
            defaultMessage: 'SIGNERS',
            description: 'Label for signers section',
          })}
        </Text>
        <Text variant="xs-dense" color="gray-600">
          {intl.formatMessage({
            id: 'UploadSignedButton.signersInstruction',
            defaultMessage:
              'Check all the signers who have signed this document.',
            description: 'Instruction for selecting signers who have signed',
          })}
        </Text>
        <ContentContainer loadingContent={{ isLoading }}>
          {isEsignatureV2RevampEnabled && errorEsignRevampRecipients && (
            <StatusMessage
              status="danger"
              message={
                errorEsignRevampRecipients instanceof Error
                  ? errorEsignRevampRecipients.message
                  : intl.formatMessage({
                      id: 'UploadSignedButton.errorMessage',
                      defaultMessage:
                        'There was a problem with retrieving recipients. Please try again.',
                      description: 'Error message for recipients section',
                    })
              }
            />
          )}
          {signers.length || externalSigners.length ? (
            <Box
              px={4}
              py={1}
              sx={{ borderRadius: 'm', boxShadow: 'depth-2', border: 'border' }}
            >
              {signers.map((user) => renderUser(user))}
              {externalSigners.map((user) => renderUser(user))}
            </Box>
          ) : null}
        </ContentContainer>
      </FlexLayout>
    );
  }

  function renderFileField() {
    return (
      <FlexLayout alignItems="flex-start" flexDirection="column" space={2}>
        <Text color="gray-900" variant="xs-dense-caps">
          {intl.formatMessage({
            id: 'UploadSignedButton.documentLabel',
            defaultMessage: 'DOCUMENT',
            description: 'Label for document section',
          })}
        </Text>
        {selectedDocument ? (
          <FlexLayout
            alignItems="center"
            bg="black-alpha-04"
            p={2}
            space={3}
            sx={{ borderRadius: 'm' }}
          >
            {documentTypeIcon(getFileExtension(selectedDocument.name))}
            <Text color="gray-900" variant="s-spaced">
              {selectedDocument.name}
            </Text>
            <IconButton
              icon="x"
              tooltip={intl.formatMessage({
                id: 'UploadSignedButton.removeTooltip',
                defaultMessage: 'remove',
                description: 'Tooltip for remove button',
              })}
              onClick={handleOnRemoveFileClick}
            />
          </FlexLayout>
        ) : (
          <Box id="add_document">
            <FileInput
              acceptedFiles={getAcceptedFiles(
                ticket.workflow,
                defaultFileExtensions,
                defaultFileExtensions,
              )}
              enableDropzone={false}
              label={intl.formatMessage({
                id: 'UploadSignedButton.attachFileLabel',
                defaultMessage: 'Attach File',
                description: 'Label for file attachment button',
              })}
              onChange={handleOnAttachFiles}
            />
          </Box>
        )}
      </FlexLayout>
    );
  }

  function renderKeepFilenameToggle() {
    return (
      testIsEverestAdmin(currentUser) && (
        <FlexLayout mt={5} space={3}>
          <Switch
            id="workflow--ticket-upload-signed-version-keep-filename"
            size="s"
            value={isKeepingUploadedFilename}
            onChange={(updatedValue) =>
              setIsKeepingUploadedFilename(updatedValue)
            }
          />
          <Text color="gray-900" variant="body2">
            {intl.formatMessage({
              id: 'UploadSignedButton.keepFilenameLabel',
              defaultMessage: 'Keep file name after upload',
              description: 'Label for keeping original filename after upload',
            })}
          </Text>
        </FlexLayout>
      )
    );
  }

  function renderFileModal() {
    return (
      <Modal
        primaryAction={{
          text: actionButton?.text,
          variant: actionButton?.variant,
          disabled: actionButton?.disabled,
          isLoading: actionButton?.isLoading,
          tooltip: actionButton?.tooltip,
          onClick: async () => {
            try {
              await actionButton?.promise();
              setShowModal(false);
            } catch (error) {
              actionButton?.errorHandler(error);
            }
          },
        }}
        cancelText={null}
        title={intl.formatMessage({
          id: 'UploadSignedButton.modalTitle',
          defaultMessage: 'Upload a Signed Document',
          description: 'Modal title for uploading signed document',
        })}
        isVisible={showModal}
        onHide={() => {
          setSelectedDocument();
          setSelectedSigners(initialSelectedSigners);
          setShowModal(false);
        }}
      >
        <FlexLayout flexDirection="column" space={8}>
          {renderFileField()}
          {renderUsersField()}
          {renderKeepFilenameToggle()}
        </FlexLayout>
      </Modal>
    );
  }

  const hasInvalidSigners = isEsignatureV2RevampEnabled
    ? testHasInvalidSignersEsignRevamp(esignRevampRecipients.signers)
    : testHasInvalidSigners(ticket, users);

  return (
    <>
      <Button
        disabled={hasInvalidSigners}
        isLoading={signers.length && !isSignersReady}
        onClick={() => setShowModal(true)}
        text={intl.formatMessage({
          id: 'UploadSignedButton.uploadSignedButton',
          defaultMessage: 'Upload Signed',
          description: 'Button text for uploading signed document',
        })}
        variant="primary"
        tooltip={
          hasInvalidSigners
            ? intl.formatMessage({
                id: 'UploadSignedButton.invalidSignersTooltip',
                defaultMessage: 'Please assign all signers',
                description: 'Tooltip for invalid signers',
              })
            : undefined
        }
      />
      {renderFileModal()}
    </>
  );
}

const mapStateToProps = ({ ticket }) => ({
  approvals: ticket.approvals,
  phases: ticket.phases,
  ticket,
});

export default connect(mapStateToProps, { ticketStagesSet })(
  UploadSignedButton,
);
